import { useEffect, useMemo } from "react";

import useHelpers from "../../../../../../../shared/services/helpers/useHelpers.hook";
import { ColDef, GridApi, NewValueParams } from "ag-grid-community";
import { GROUP_FUNCTION } from "../../../../../../../shared/contracts/TGroupFunctions";
import IColumnAndFunction from "../../../../../../../shared/contracts/IColumnAndFunction";
import { TGridApi } from "../../../../../../../shared/contracts/TGridApi";
import IReportSelectedColumn from "../../../../../../../shared/contracts/IReportSelectedColumn";
import IReportAvailableColumn from "../../../../../../../shared/contracts/IReportAvailableColumn";
import useWizardReportDataContext from "../../../../contexts/useWizardReportData.context";
import WizardReportFieldsRowActionsRenderer from "./wizard-report-fields-row-actions.renderer";

/**
 * @author Pradeep R <pradeep.rajendran@rldatix.com>
 * @createdOn Dec/01/2024
 */
export default function useWizardReportFieldsService() {

    const { checkIsDifferentOnSameType, extractKeys, getKeyByValue, extractEnumKeys, createMappings } = useHelpers();
    const NONE_KEY = getKeyByValue(GROUP_FUNCTION, GROUP_FUNCTION.NONE)!;
    const GROUP_KEY = getKeyByValue(GROUP_FUNCTION, GROUP_FUNCTION.GROUP)!;
    const { wizardData, availableColumns, applyReportData } = useWizardReportDataContext();


    const getFieldsColumnOptions = () => {
        // UPDATE: missing column names should not be added
        // reason: if the user changes the data source,. 
        // then the current columns that are selected for the current DataSource
        // should not be included in the dropdown,..


        // strangely: some selected columns are not present in the available columns,
        // this is a data-issue and has to be fixed at the api-side
        const missingCols: IReportAvailableColumn[] = [];
        // wizardData.reportSpecification.columnSelections.forEach(selColumn => {
        //     if (availableColumns.findIndex(x => x.columnName === selColumn.columnName) === -1) {
        //         missingCols.push({ ...selColumn });
        //     }
        // });

        // ---
        return createMappings(
            [...availableColumns, ...missingCols], "columnName", "columnDescription",
            { key: '-1', value: '...' }
        );
    }

    const updateWizardData = (selectedColumns: IReportSelectedColumn[]) => {
        // --- update the data-source

        if (checkIsDifferentOnSameType(
            selectedColumns,
            wizardData.reportSpecification.columnSelections,
            ["columnName","columnDescription"]
        )) {
            applyReportData({
                reportSpecification: {
                    ...wizardData.reportSpecification,
                    columnSelections: selectedColumns
                }
            })
        }
        // else {
        //     console.log('test');
        // }
    }

    const convertToSelectedColumn = (nodeData: IColumnAndFunction): IReportSelectedColumn => {
        let selCol = {} as IReportSelectedColumn;
        const columnName = nodeData.field;
        const hasFunction = nodeData.function !== NONE_KEY;
        let targetCol = availableColumns.find(x => x.columnName === columnName)
        if (targetCol) {
            selCol = {
                ...targetCol,
                columnDataType: null,
                // ---
                aggregateFunction: hasFunction ? nodeData.function : null,
                orderType: hasFunction ? 'ASC' : null,
                groupBy: hasFunction ? 'True' : null,
                columnDescription: nodeData.description  ,
            } as IReportSelectedColumn;
        }
        return selCol
    }

    const updateWizardDataWithValidData = (validNodeDataList: IColumnAndFunction[]) => {
        const selCols: IReportSelectedColumn[] = [];
        validNodeDataList.forEach(nodeData => {
            selCols.push(
                convertToSelectedColumn(nodeData)
            );
        });

        //--- update the wizardData
        updateWizardData(selCols);
    }

    const handleGridDataChange = (event: NewValueParams<IColumnAndFunction, any>) => {
        let gridData = event.api.getRenderedNodes();
        if (gridData) {
            let selectedColumns: IReportSelectedColumn[] = [];
            gridData.forEach(node => {
                if (node.data) {
                    selectedColumns.push(
                        convertToSelectedColumn(node.data)
                    );
                }
            });

            // --- update the WizardData
            updateWizardData(selectedColumns);
        }
    }

    const handleGroupFunctionChange = (
        e: NewValueParams<IColumnAndFunction, any>
    ) => {
        const currentFunctionKey = e.newValue as string;
        const currentRowIndex = e.node?.rowIndex;
        if (e.node && currentFunctionKey === NONE_KEY) {
            let isOtherNodesGrouped = false;
            e.api.forEachNode((otherNode, otherRowIndex) => {
                if (otherRowIndex !== currentRowIndex &&
                    otherNode.data && otherNode.data['function'] !== NONE_KEY) {
                    isOtherNodesGrouped = true;
                }
            });
            // re-applies the old value if (...) is selected
            // if (isOtherNodesGrouped && event.node?.data) {
            //     event.node.data['function'] = event.oldValue;
            //     event.api.refreshCells();
            // }
        } else {
            e.api.forEachNode((otherNode, otherRowIndex) => {
                if (otherRowIndex !== currentRowIndex &&
                    otherNode.data && otherNode.data['function'] === NONE_KEY
                ) {
                    otherNode.data['function'] = GROUP_KEY;
                }
            });
            e.api.refreshCells();
        }

        // --- update's the WizardData
        handleGridDataChange(e);
    }



    const getNewRow = (
        gridApi: TGridApi<IColumnAndFunction>
    ) => {
        const hasFunctions = isFunctionsApplied(gridApi);
        return {
            field: "-1",
            function: getKeyByValue(GROUP_FUNCTION,
                hasFunctions ? GROUP_FUNCTION.GROUP : GROUP_FUNCTION.NONE)!,
        };
    };

    const initializeRows = (
        gridApi: TGridApi<IColumnAndFunction>,
        selectedColumns: IReportSelectedColumn[]
    ): IColumnAndFunction[] => {
        let newRows: IColumnAndFunction[] = [];
        if (gridApi) {

            // clear residues
            gridApi.updateGridOptions({ rowData: [] })

            // only add columns if its in the available column-list
            const availColNames = availableColumns.map(x => x.columnName);
            newRows = selectedColumns.filter(
                selCol => availColNames.includes(selCol.columnName)
            ).map((x) => ({
                field: x.columnName,
                function: x.aggregateFunction ? x.aggregateFunction :
                    getKeyByValue(GROUP_FUNCTION, GROUP_FUNCTION.NONE)!,
                    description: x.columnDescription ?? undefined,
            }))

            const length = gridApi.getDisplayedRowCount();
            gridApi.applyTransaction({
                addIndex: length,
                add: [...newRows],
            });

            // refresh grid
            gridApi.refreshCells();
        }

        //--- return new Rows
        return newRows;
    };

    const insertRow = (
        gridApi: TGridApi<IColumnAndFunction>,
        rowIndex?: number | null
    ) => {
        if (gridApi) {
            const length = gridApi.getDisplayedRowCount();
            gridApi.applyTransaction({
                addIndex: (rowIndex === undefined || rowIndex === null) ? length : rowIndex,
                add: [getNewRow(gridApi)],
            });
            gridApi.refreshCells();
        }
    }

    const removeRow = (
        gridApi: TGridApi<IColumnAndFunction>,
        nodeData?: IColumnAndFunction
    ) => {
        if (gridApi && nodeData) {
            gridApi.applyTransaction({
                remove: [nodeData]
            });
        }
    }

    const isFunctionsApplied = (gridApi: TGridApi<IColumnAndFunction>) => {
        let hasFunction: boolean = false;

        if (gridApi) {
            gridApi.forEachNode(node => {
                if (node.data && node.data['function'] !== NONE_KEY) {
                    hasFunction = true;
                }
            });
        }

        return hasFunction;
    }

    const getColDefs = useMemo((): ColDef<IColumnAndFunction>[] => {

        let _fieldDataSource = getFieldsColumnOptions();
        return [
            {
                rowDrag: true,
                headerName: "Actions", editable: false,
                resizable: false, minWidth: 224,
                cellRenderer: WizardReportFieldsRowActionsRenderer,
            },
            {
                editable: true, field: "field", headerName: "Column Name", flex: 2,
                cellEditor: "agSelectCellEditor", //'agRichSelectCellEditor',
                refData: _fieldDataSource, // dropdown-dataSource
                cellEditorParams: {
                    values: extractKeys(_fieldDataSource), // extract the keys
                    allowTyping: true, filterList: true, highlightMatch: true,
                    // valueListMaxHeight: 220,
                },
                onCellValueChanged: (event) => {
                const newColumnName = event.newValue;
                const updatedColumn = availableColumns.find(col => col.columnName === newColumnName);
                if (updatedColumn) {
                    event.data.description = updatedColumn.columnDescription ?? undefined;
                    handleGridDataChange(event); 
                }
                }
            },
            {
                editable: true, field: "description", headerName: "Description", flex: 2,
                cellEditor: "agTextCellEditor", 
                valueFormatter: (params) => params.value || params?.data?.field, 
                onCellValueChanged: (event) => {
                    const updatedDescription = event.newValue;
                    const updatedRowData = event.data;
                    if (updatedDescription !== event.oldValue) {
                        updatedRowData.description = updatedDescription;
                        handleGridDataChange(event);
                    }
                }
            },
            {
                editable: true, field: "function", headerName: "Group Function", flex: 1,
                cellEditor: "agSelectCellEditor",
                refData: GROUP_FUNCTION, // dropdown-dataSource
                cellEditorParams: {
                    allowTyping: true, filterList: true, highlightMatch: true,
                    values: extractEnumKeys(GROUP_FUNCTION),
                    // valueListMaxHeight: 220,
                },
                cellRendererParams: {
                    onAddRowAbove: (rowIndex: number) => { },
                    onAddRowBelow: (rowIndex: number) => { },
                    onRemoveRow: (rowIndex: number) => { },
                },
                onCellValueChanged: handleGroupFunctionChange,
            },
        ];
    }, [availableColumns]);




    // ---
    return {
        getColDefs,
        updateWizardDataWithValidData,
        initializeRows, insertRow, removeRow, getNewRow,
        isFunctionsApplied,
    };
}